import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { ConditionClause, ConfigurationData } from '../../types';
import { convertVisualToText } from '../../utils/expressionConverterBackend';

interface ExpressionPreviewProps {
  conditions: ConditionClause[];
  visible: boolean;
  onToggle: () => void;
  onValidate?: () => void;
  className?: string;
  config?: ConfigurationData | null;
}

const ExpressionPreview: React.FC<ExpressionPreviewProps> = ({
  conditions,
  visible,
  onToggle,
  onValidate,
  className,
  config,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // Generate expression from applied conditions only
  const expression = useMemo(() => {
    const appliedConditions = conditions.filter(
      clause => clause.isValid !== false && clause.element && clause.operator
    );

    if (appliedConditions.length === 0) {
      return '';
    }

    try {
      return convertVisualToText(appliedConditions, config);
    } catch (error) {
      return `Error generating expression: ${error}`;
    }
  }, [conditions, config]);

  const handleCopyToClipboard = async () => {
    if (!expression) return;

    try {
      await navigator.clipboard.writeText(expression);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const hasExpression = expression.length > 0;

  return (
    <Paper
      className={className}
      sx={{
        mb: 2,
        overflow: 'hidden',
        borderLeft: visible ? '4px solid #1976d2' : 'none',
      }}
    >
      {/* Header with toggle and actions */}
      <Box
        sx={{
          p: 2,
          backgroundColor: '#fafafa',
          borderBottom: visible ? '1px solid #e0e0e0' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" sx={{ p: 0.5 }}>
            {visible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="subtitle2" color="text.secondary">
            Expression Preview
          </Typography>
          {!visible && (
            <IconButton size="small" sx={{ p: 0.5 }}>
              {hasExpression ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          )}
        </Box>

        {visible && hasExpression && (
          <Box sx={{ display: 'flex', gap: 1 }} onClick={e => e.stopPropagation()}>
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                size="small"
                onClick={handleCopyToClipboard}
                color={copySuccess ? 'success' : 'default'}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
            {onValidate && (
              <Button
                size="small"
                variant="outlined"
                onClick={onValidate}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                Validate
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Collapsible content */}
      <Collapse in={visible}>
        <Box sx={{ p: 2 }}>
          {hasExpression ? (
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              value={expression}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                style: {
                  fontFamily: '"Monaco", "Consolas", "Courier New", monospace',
                  fontSize: '14px',
                  backgroundColor: '#f8f9fa',
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                },
              }}
            />
          ) : (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                color: 'text.secondary',
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px dashed #e0e0e0',
              }}
            >
              <Typography variant="body2">
                No conditions applied yet. Add and apply conditions to see the generated expression.
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ExpressionPreview;
